import type { MapId } from '~/_shared/types/map';
import type { BoundaryCombineGeometryRequestItem } from '~/store/boundaries/boundaries.repository';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  type AreaQueryBoundaryFilterRequest, getMapBoundaryAreaQuery, type MapBoundaryAreaQueryResponseWithBoundaries,
} from '~/store/boundaryItems/boundaryItems.repository';
import { BoundaryMatchingType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';

export const getOverlappingBoundaries = async (
  clientId: number,
  mapId: MapId,
  geometry: readonly BoundaryCombineGeometryRequestItem[],
  boundaryGroupId: BoundaryGroupId,
): Promise<MapBoundaryAreaQueryResponseWithBoundaries> => {
  const geometryTypeSelections = geometry
    .filter(geometry => geometry.type === 'geometry');
  const boundaryTypeSelections = geometry
    .filter(geometry => geometry.type === 'boundary');

  const areaQueryResponse = await getMapBoundaryAreaQuery(clientId, {
    map_id: mapId,
    exclude_artificial: false,
    format: 'json',
    overlap_type: 'partial',
    boundary_group_id: boundaryGroupId,
    ...geometryTypeSelections.length ? {
      polygon_filter: {
        type: 'or',
        polygons: geometryTypeSelections.map(geometry => ({ path: geometry.value })),
      },
    } : undefined,
    ...boundaryTypeSelections.length ? {
      boundary_filter: {
        type: 'or',
        boundaries: boundaryTypeSelections.reduce((boundaryFilter, boundarySelection) => {
          const thisBoundaryFilter = boundaryFilter[boundarySelection.boundary_group_id];

          boundaryFilter[boundarySelection.boundary_group_id] = thisBoundaryFilter ? {
            ...thisBoundaryFilter,
            boundary_ids: [...thisBoundaryFilter.boundary_ids, boundarySelection.boundary_id],
          } : {
            boundary_ids: [boundarySelection.boundary_id],
            matchings: {
              matching_type: BoundaryMatchingType.Geometry,
            },
          };

          return boundaryFilter;
        }, {} as AreaQueryBoundaryFilterRequest['boundaries']),
      },
    } : undefined,
  });

  if (('boundaries' in areaQueryResponse)) {
    return areaQueryResponse;
  }
  throw new Error('Unexpected response from getMapBoundaryAreaQuery');
};
